import { format, isSameMonth } from "date-fns";
import { coerceJournalTagForDisplay, type JournalLibraryTag } from "@/lib/journal-tags";

export interface JournalStatsEntry {
  tag: string;
  createdAt: string;
}

export interface ThemeCount {
  tag: JournalLibraryTag;
  count: number;
}

export interface JournalStats {
  monthCount: number;
  allTimeCount: number;
  /**
   * Unique calendar days within referenceDate's month that hold at least one
   * entry. No consecutiveness is required, gaps are irrelevant, and it is scoped
   * to the browsed month rather than anchored to today. Two entries on one day
   * count once.
   */
  reflectionDays: number;
  topTheme: JournalLibraryTag | null;
  /** Theme breakdown for referenceDate's month, most-frequent first. */
  themeCounts: ThemeCount[];
}

/** Shared by the surfaces that report entry stats, so "this month" / reflection
 * days / top theme can never drift between them. */
export function computeJournalStats(journals: JournalStatsEntry[], referenceDate: Date): JournalStats {
  const inReferenceMonth = journals.filter((j) => isSameMonth(new Date(j.createdAt), referenceDate));

  // Local-time day keys, matching how the calendar buckets entries into squares —
  // so this number always agrees with the dotted days the user can see.
  const reflectionDays = new Set(
    inReferenceMonth.map((j) => format(new Date(j.createdAt), "yyyy-MM-dd")),
  ).size;

  const tagCounts: Partial<Record<JournalLibraryTag, number>> = {};
  inReferenceMonth.forEach((j) => {
    const tag = coerceJournalTagForDisplay(j.tag);
    tagCounts[tag] = (tagCounts[tag] ?? 0) + 1;
  });
  const themeCounts = (Object.entries(tagCounts) as [JournalLibraryTag, number][])
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count);

  return {
    monthCount: inReferenceMonth.length,
    allTimeCount: journals.length,
    reflectionDays,
    topTheme: themeCounts[0]?.tag ?? null,
    themeCounts,
  };
}
